import React, { Component, type JSX } from 'react';
import { Formik, FormikValues, Form, FormikProps } from 'formik';
import { Input } from '../../formik/Input';
import { Textarea } from '../../formik/Textarea';
import { RadioGroup } from '../../formik/RadioGroup';
import { validateEmail, validatePhone, validateRequired } from '../../formik/validate.formik';
import { post } from '../../util/ApiWrapper';
import { FormErrors } from '../../formik/FormErrors';
import { AxiosError } from 'axios';


interface ComponentProps {
  type: string;
  redirect: string;
}

interface ComponentState {
  initialValues: FormikValues;
  error?: ErrorResponse['errors'] | string;
  redirecting: boolean;
}

const initialFormValues: FormikValues = {
  firstName: '',
  lastNamePrefix: '',
  lastName: '',
  email: '',
  phone: '',
  remark: '',
};

export interface ErrorResponse {
  errors?: string[];
}

/**
 * This react component renders the intake form. The form
 * type is set in ACF in the twig template, and passed to
 * the backend. Formik handles the frontend validation.
 * These messages are shown under the input fields. The
 * backend validation errors are shown on top.
 *
 * @example
 * <app-form-intake-bijles type="{{ item.form }}"></app-form-intake-bijles>
 */
export class FormIntakeBijlesVwo extends Component<ComponentProps, ComponentState> {

  public state: ComponentState = {
    initialValues: initialFormValues,
    error: null,
    redirecting: false,
  };

  /**
     * Submit the form contents, append the form type as set
     * in ACF to the request. On success redirect to the url
     * set in ACF.
     */
  public async onSubmit(values: FormikValues): Promise<void> {
    const { redirect, type } = this.props;

    // reset validation errors.
    this.setState({ error: null });

    try {
      await post<void>({
        controller: 'IntakeForm',
        method: 'submitForm',
        data: {
          form: {
            type: type,
            ...values,
          },
        },
      });

      if (redirect) {
        this.redirectTo(redirect);
      }
    } catch (e) {
      const errors: AxiosError<ErrorResponse> = e;
      this.showError(errors.response.data.errors || 'Er is iets misgegaan!');
    }
  }

  /**
     * Redirect to the url set by ACF.
     */
  // istanbul ignore next
  public redirectTo(url: string): void {
    this.setState({ redirecting: true });
    window.location.href = url;
  }

  /**
     * Scroll to the top of the page and pass the error to the state
     * so it will be displayed at the top of the form.
     */
  private showError(error: ErrorResponse['errors'] | string): void {
    this.setState({
      error: error,
    });
  }

  /**
     * Render the form.
     */
  public render(): JSX.Element {
    const { error, initialValues, redirecting } = this.state;


    return (
      <div className={'c-intake-form'}>

        <Formik initialValues={initialValues}
          onSubmit={(v: FormikValues): Promise<void> => this.onSubmit(v)}>
          {({ isSubmitting }: FormikProps<FormikValues>): JSX.Element => (
            <Form style={{
              display: 'block',
            }}>
              {error && <FormErrors messages={error}/>}

              <div className="bijles-vwo main-blok">
                <h3 className="bijles-vwo-down">Selecteer hieronder het gewenste vak:</h3>
                <div className="niveaublok">
                  <RadioGroup name="remark" items={[
                    { value: 'VWO Aardrijkskunde', label: 'Aardrijkskunde' },
                    { value: 'VWO Bedrijfseconomie', label: 'Bedrijfseconomie' },
                    { value: 'VWO Biologie', label: 'Biologie' },
                    { value: 'VWO Duits', label: 'Duits' },
                    { value: 'VWO Economie', label: 'Economie' },
                    { value: 'VWO Engels', label: 'Engels' },
                    { value: 'VWO Frans', label: 'Frans' },
                    { value: 'VWO Geschiedenis', label: 'Geschiedenis' },
                    { value: 'VWO Nederlands', label: 'Nederlands' },
                    { value: 'VWO Natuurkunde', label: 'Natuurkunde' },
                    { value: 'VWO Scheikunde', label: 'Scheikunde' },
                    { value: 'VWO Wiskunde', label: 'Wiskunde' },
                    { value: 'VWO onbekend', label: 'Iets anders' },
                  ]} />
                </div>
              </div>
              <div className="bijles-form-1">

                <Input cssClass="form-control--no-border bijlesname"
                  name="firstName"
                  placeholder="Voornaam"
                  validate={validateRequired}
                />


                <Input cssClass="form-control--no-border bijlesprefix"
                  name="lastNamePrefix"
                  placeholder="Tussenvoegsel"
                />

                <Input cssClass="form-control--no-border bijleslastname"
                  name="lastName"
                  placeholder="Achternaam"
                  validate={validateRequired}
                />

              </div>
              <div className="bijles-form-2">

                <Input cssClass="form-control--no-border bijlesemail"
                  name="email"
                  placeholder="E-mailadres"
                  validate={(v: string): string => validateRequired(v) || validateEmail(v)}
                />

                <Input cssClass="form-control--no-border bijlestelephone"
                  name="phone"
                  placeholder="Telefoonnummer"
                  validate={(v: string): string => validateRequired(v) || validatePhone(v)}
                />

              </div>

              <div className="bijles-aanvraag">
                <div className="bijles-aanvraag-1">
                  <p>Bijles aanvragen voor:</p>
                  <Textarea cssClass="bijles-form-level form-control--no-border mw-100"
                    rows={1}
                    name="remark"
                    placeholder=""
                  />
                  <button className="btn btn-primary gtm-ext-book-payment ml-auto bijles-button"
                    disabled={isSubmitting || redirecting}
                    type="submit">
                      Aanmelden
                    {(isSubmitting || redirecting) && <i className="fa fa-spinner fa-pulse"/>}
                  </button>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    );
  }

}
